import React from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

const daoFeatures = [
  {
    title: "Gouvernance Communautaire (À venir)",
    description: "À terme, la communauté pourra voter sur les propositions importantes de la plateforme",
    icon: "🗳️",
    color: "cyan"
  },
  {
    title: "Propositions Ouvertes",
    description: "Tous les membres peuvent déjà suggérer des améliorations via nos canaux de feedback",
    icon: "💡",
    color: "emerald"
  },
  {
    title: "Transparence & Écoute",
    description: "Nous partageons ouvertement nos décisions et écoutons activement la communauté",
    icon: "🔍",
    color: "cyan"
  },
  {
    title: "Reconnaissance des Contributeurs",
    description: "Les membres actifs et contributeurs sont reconnus et valorisés dans la communauté",
    icon: "🎁",
    color: "emerald"
  }
];

const jupGroups = [
  {
    name: "Utilisateurs",
    description: "La base de la communauté. Tous les membres qui utilisent Jupipotes quotidiennement, partagent du contenu et interagissent entre eux.",
    icon: "👥",
    role: "Participer, créer du contenu, voter sur les propositions",
    benefits: "Accès à toutes les fonctionnalités, tokens JUP pour l'activité"
  },
  {
    name: "Équipe Core",
    description: "Les développeurs et designers qui construisent et maintiennent la plateforme Jupipotes.",
    icon: "🛠️",
    role: "Développer, corriger bugs, implémenter nouvelles fonctionnalités",
    benefits: "Influence technique, tokens JUP, reconnaissance communautaire"
  },
  {
    name: "DAO Jupiter",
    description: "L'organisation autonome décentralisée qui prend les décisions stratégiques pour l'avenir de Jupipotes.",
    icon: "🪐",
    role: "Voter sur propositions majeures, définir la direction stratégique",
    benefits: "Pouvoir de décision, influence sur la roadmap"
  },
  {
    name: "Catdets",
    description: "Les ambassadeurs et membres les plus actifs qui aident à faire grandir la communauté et à accueillir les nouveaux membres.",
    icon: "🚀",
    role: "Modération, aide aux nouveaux, animation communautaire",
    benefits: "Badges spéciaux, tokens bonus, accès anticipé aux features"
  },
  {
    name: "Working Groups",
    description: "Des équipes spécialisées qui travaillent sur des projets spécifiques comme le marketing, le contenu ou les partenariats.",
    icon: "🤝",
    role: "Exécuter projets spécifiques, coordonner initiatives",
    benefits: "Autonomie sur projets, budget dédié, tokens pour résultats"
  }
];

const votingProcess = [
  {
    step: 1,
    title: "Soumission de Proposition",
    description: "Un membre soumet une idée ou proposition via le système de gouvernance"
  },
  {
    step: 2,
    title: "Discussion Communautaire",
    description: "La communauté discute et débat de la proposition pendant 7 jours"
  },
  {
    step: 3,
    title: "Vote",
    description: "Les détenteurs de tokens JUP votent pour ou contre pendant 5 jours"
  },
  {
    step: 4,
    title: "Exécution",
    description: "Si approuvée, la proposition est mise en œuvre par l'équipe technique"
  }
];

const achievements = [
  {
    title: "Lancement de la plateforme Jupipotes",
    date: "Janvier 2025",
    description: "Mise en ligne de la version beta de la plateforme sociale"
  },
  {
    title: "Système de communautés",
    date: "Février 2025",
    description: "Déploiement des communautés thématiques et système de modération"
  },
  {
    title: "Messagerie temps réel",
    date: "Mars 2025",
    description: "Implémentation du chat privé et des conversations"
  },
  {
    title: "Première centaine de membres",
    date: "Avril 2025",
    description: "Croissance organique avec les premiers early adopters"
  },
  {
    title: "500 membres actifs",
    date: "Juin 2025",
    description: "Franchissement du cap symbolique des 500 utilisateurs"
  },
  {
    title: "Intégration vidéo et médias",
    date: "Septembre 2025",
    description: "Support YouTube et upload vidéo natif"
  },
  {
    title: "1 000+ membres actifs",
    date: "Octobre 2025",
    description: "Communauté grandissante et engagée"
  },
  {
    title: "Milestone actuel",
    date: "Octobre 2025",
    description: "Optimisation continue et amélioration de l'expérience utilisateur"
  }
];

const CommunityDAO: React.FC = () => {
  return (
    <div className={`min-h-screen w-full ${gradientBg} py-8 px-4`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img 
              src="https://support.jup.ag/hc/article_attachments/20007956605852" 
              alt="J.U.P" 
              className="w-32 h-32 rounded-full border-4 border-cyan-400 shadow-2xl"
            />
          </div>
          <h1 className="text-6xl font-extrabold text-emerald-100 mb-4">
            La Communauté J.U.P
          </h1>
          <p className="text-2xl text-cyan-300 font-semibold mb-2">
            Jupiter United Planet
          </p>
          <p className="text-xl text-emerald-200 max-w-3xl mx-auto">
            Une communauté décentralisée et collaborative qui façonne ensemble l'avenir de Jupipotes
          </p>
        </div>

        {/* Vision J.U.P */}
        <section className={`${glassCard} mb-12`}>
          <h2 className="text-3xl font-bold text-cyan-300 mb-6 flex items-center gap-3">
            <span className="text-4xl">🌍</span>
            La Vision du J.U.P
          </h2>
          <div className="space-y-4 text-emerald-200 text-lg leading-relaxed">
            <p>
              Le <strong className="text-cyan-300">J.U.P (Jupiter United Planet)</strong> représente notre vision d'une communauté 
              véritablement décentralisée où chaque voix compte et où la collaboration prime sur la hiérarchie.
            </p>
            <p>
              Plutôt qu'une structure traditionnelle top-down, le J.U.P est conçu comme un <strong className="text-emerald-300">écosystème 
              synergique</strong> où différents groupes travaillent ensemble de manière autonome mais coordonnée.
            </p>
            <p>
              C'est une <strong className="text-cyan-300">expérience d'exécution stratégique distribuée</strong> : chaque groupe a ses 
              responsabilités et son autonomie, mais tous partagent la même vision d'une plateforme sociale décentralisée et équitable.
            </p>
          </div>
        </section>

        {/* Les 5 Groupes du J.U.P */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 text-center">
            Les 5 Piliers du J.U.P
          </h2>
          <div className="space-y-6">
            {jupGroups.map((group, idx) => (
              <div key={idx} className={`${glassCard} hover:border-cyan-400/50 transition-all duration-300`}>
                <div className="flex items-start gap-6">
                  <div className="text-6xl flex-shrink-0">{group.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-emerald-100 mb-2">{group.name}</h3>
                    <p className="text-emerald-200 mb-4 text-lg">{group.description}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-cyan-900/20 rounded-lg p-4 border border-cyan-700/30">
                        <h4 className="text-cyan-300 font-semibold mb-2">Rôle</h4>
                        <p className="text-emerald-200 text-sm">{group.role}</p>
                      </div>
                      <div className="bg-emerald-900/20 rounded-lg p-4 border border-emerald-700/30">
                        <h4 className="text-emerald-300 font-semibold mb-2">Avantages</h4>
                        <p className="text-emerald-200 text-sm">{group.benefits}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* DAO Features */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 text-center">
            Vision de la Gouvernance Future
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {daoFeatures.map((feature, idx) => (
              <div key={idx} className={`${glassCard} hover:scale-105 transition-transform duration-300`}>
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-emerald-100 mb-3">{feature.title}</h3>
                <p className="text-emerald-200">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Processus de Vote */}
        <section className={`${glassCard} mb-12 relative`}>
          <div className="absolute top-4 right-4 bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-bold border border-purple-500/50">
            📅 Prévu 2026
          </div>
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 flex items-center gap-3">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Comment fonctionnera le vote (Futur)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {votingProcess.map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-gradient-to-br from-cyan-900/40 to-emerald-900/40 rounded-xl p-6 border border-cyan-700/30 h-full">
                  <div className="w-12 h-12 bg-cyan-400/20 rounded-full flex items-center justify-center mb-4 border-2 border-cyan-400">
                    <span className="text-2xl font-bold text-cyan-300">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-emerald-100 mb-2">{item.title}</h3>
                  <p className="text-emerald-200 text-sm">{item.description}</p>
                </div>
                {idx < votingProcess.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Réalisations */}
        <section className={`${glassCard} mb-12`}>
          <h2 className="text-3xl font-bold text-cyan-300 mb-8 flex items-center gap-3">
            <span className="text-4xl">🏆</span>
            Nos Réalisations
          </h2>
          <div className="space-y-6">
            {achievements.map((achievement, idx) => (
              <div key={idx} className="flex items-start gap-6 pb-6 border-b border-white/10 last:border-0">
                <div className="flex-shrink-0 w-32 text-right">
                  <span className="text-cyan-400 font-semibold">{achievement.date}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-emerald-100 mb-2">{achievement.title}</h3>
                  <p className="text-emerald-200">{achievement.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <div className={`${glassCard} text-center`}>
          <h3 className="text-3xl font-bold text-emerald-100 mb-4">
            Rejoignez le J.U.P
          </h3>
          <p className="text-xl text-emerald-200 mb-8 max-w-2xl mx-auto">
            Que vous soyez utilisateur, développeur, créateur de contenu ou passionné de Web3, 
            il y a une place pour vous dans le Jupiter United Planet.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/communities"
              className="px-8 py-4 bg-cyan-400/20 text-cyan-200 font-bold rounded-xl hover:bg-cyan-400/40 transition-all duration-300 flex items-center gap-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Rejoindre des Communautés
            </Link>
            <Link
              to="/about-jup"
              className="px-8 py-4 bg-emerald-700/20 text-emerald-200 font-bold rounded-xl hover:bg-emerald-700/40 transition-all duration-300 flex items-center gap-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              En savoir plus
            </Link>
          </div>
        </div>

        {/* Retour */}
        <div className="mt-8 text-center">
          <Link
            to="/JUPResources"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux ressources
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CommunityDAO;
